import React, { PropsWithChildren, ReactNode } from 'react'
import styled from '@emotion/styled'
import { KeyEvents } from 'shared/enums/keys'
import { TabPanel } from './TabPanel'
import useTabs from 'shared/state/useTabs'

export interface TabProps extends PropsWithChildren<unknown> {
  index: number
  // Used by emotion to allow styling by parent (using styled() function)
  className?: string
  // can choose to not render the tab completely if the tab is not active or simply hide it - this allows for the preservation of formik state for example if only hiding
  renderButHide?: boolean
}

export const Tab = ({ index, children, className }: TabProps) => {
  const { activeTab, setActiveTab } = useTabs()

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (KeyEvents.spacebar === e.key) {
      setActiveTab(index)
    }
  }

  const handleOnClick = () => {
    setActiveTab(index)
  }

  return (
    <StyledTab
      role="tab"
      id={`tab-${index}`}
      aria-selected={activeTab === index}
      aria-controls={`tabpanel-${index}`}
      isActive={activeTab === index}
      onClick={handleOnClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className={className}
    >
      {children}
    </StyledTab>
  )
}

export interface TabListProps {
  children: ReactNode
  label: string
  // Used by emotion to allow styling by parent (using styled() function)
  className?: string
}

export const TabList = ({ children, label, className }: TabListProps) => {
  return (
    <StyledTabList role="tablist" aria-label={label} className={className}>
      {children}
    </StyledTabList>
  )
}

export { TabPanel }

const StyledTab = styled.a<{ isActive?: boolean }>(({ theme, isActive }) => ({
  ...theme.fontStyle.h5,
  textAlign: 'center',
  width: '100%',
  textDecoration: 'none',
  userSelect: 'none',
  borderRadius: theme.radii[5],
  padding: theme.mixins.pxSpread([theme.space[2], theme.space[4]]),
  color: isActive ? theme.colors.text.primary : theme.colors.text.secondary,
  backgroundColor: isActive ? theme.colors.white : 'transparent',
  transition: `all ${theme.transitionTime[1]}s ease`,
  '&:hover': {
    color: theme.colors.text.primary
  },
  whiteSpace: 'nowrap'
}))

const StyledTabList = styled.div(({ theme }) => ({
  display: 'inline-flex',
  flexDirection: 'row',
  gap: theme.space[5],
  backgroundColor: theme.colors.tabs.tabList.background,
  padding: theme.space[1],
  borderRadius: theme.radii[5],
  overflow: 'auto',
  width: '100%',
  [theme.mediaQueries.md]: {
    width: 'auto'
  }
}))
